// Unpublished Work © 2023-2024 Deere & Company.

import { useContext } from "react";

import {
  UpdateShipToAddressResponse,
  UpdateShipToAddressV2Response,
  UserPreferenceResponse,
} from "./xtremeBffInterfaces";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";
import {
  OrderReviewResponse2,
  OrderUpdateResponse,
} from "../../pages/orders/types";
import { DealerAccount, DealerDiscount } from "../dealer/types";
import { OrderProcessRequest, SavedAddress } from "../orders/orderInterfaces";
import {
  LineValidationRequest,
  LineValidationResponse,
} from "../validation/validationInterfaces";

const useXtremeBffApi = () => {
  const axios = useContext(ApiContext);
  const { XTREME_BFF_SERVICE_URL: baseUrl } = useContext(ConfigContext);
  return {
    updateOrder: async (
      xtremeBffServiceURL: string,
      accessToken: string,
      idToken: string,
      payload: OrderProcessRequest,
      lang: string
    ): Promise<OrderUpdateResponse> =>
      axios
        .postWithRetries<OrderUpdateResponse>(
          `${xtremeBffServiceURL}/orders/process`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "X-Id-Token": idToken,
            },
            params: {
              userAction: "update",
              client: "UI",
              lang,
            },
          }
        )
        .then((response) => response.data)
        .catch((error) => Promise.reject(error)),

    reviewOrder: async (
      xtremeBffServiceURL: string,
      accessToken: string,
      idToken: string,
      payload: OrderProcessRequest,
      lang: string
    ): Promise<OrderReviewResponse2> =>
      axios
        .postWithRetries<OrderReviewResponse2>(
          `${xtremeBffServiceURL}/orders/process`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "X-Id-Token": idToken,
            },
            params: {
              userAction: "review",
              client: "UI",
              lang,
            },
          }
        )
        .then((response) => response.data)
        .catch((error) => Promise.reject(error)),
    updateShipToAddress: (
      xtremeBffServiceURL: string,
      accessToken: string,
      idToken: string,
      shipToAddress: SavedAddress,
      orderNumber: string,
      accountId: string,
      lang?: string
    ): Promise<UpdateShipToAddressResponse> =>
      axios
        .patchWithRetries(
          `${xtremeBffServiceURL}/orders/updateShipToAddress/${orderNumber}`,
          shipToAddress,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "X-Id-Token": idToken,
            },
            params: { accountId, lang },
          }
        )
        .then((response) => response.data)
        .catch((error) => Promise.reject(error)),

    updateShipToAddressWithCleanser: (
      xtremeBffServiceURL: string,
      accessToken: string,
      idToken: string,
      shipToAddress: SavedAddress,
      orderNumber: string,
      accountId: string,
      lang?: string
    ): Promise<UpdateShipToAddressResponse> =>
      axios
        .patchWithRetries(
          `${xtremeBffServiceURL}/orders/updateShipToAddressWithCleanser/${orderNumber}`,
          shipToAddress,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "X-Id-Token": idToken,
            },
            params: { accountId, lang },
          }
        )
        .then((response) => response.data)
        .catch((error) => Promise.reject(error)),

    updateShipToAddressV2: (
      xtremeBffServiceURL: string,
      shipToAddress: SavedAddress,
      orderNumber: string,
      accountId: string,
      lang?: string,
      selectedFromSuggestion?: boolean,
      userConfirmed?: boolean
    ): Promise<UpdateShipToAddressV2Response> =>
      axios
        .patchWithRetries(
          `${xtremeBffServiceURL}/orders/v2/updateShipToAddress/${orderNumber}`,
          shipToAddress,
          {
            params: { accountId, lang, selectedFromSuggestion, userConfirmed },
          }
        )
        .then((response) => response.data)
        .catch((error) => Promise.reject(error)),

    async getAccountsDetails(
      xtremeBffServiceURL: string,
      accountIds: string[],
      language: string
    ): Promise<DealerAccount[]> {
      const { data } = await axios.getWithRetries<DealerAccount[]>(
        `${xtremeBffServiceURL}/dealer/accounts?accountIds=${accountIds.join(
          ","
        )}&lang=${language}`
      );
      return data;
    },
    getDealerSelectionDiscountsSet: (
      xtremeBffServiceURL: string,
      accountId: string
    ): Promise<DealerDiscount[]> =>
      axios
        .getWithRetries<DealerDiscount[]>(
          `${xtremeBffServiceURL}/dealer/selectionDiscountsSet`,
          {
            params: { accountId },
          }
        )
        .then((response) => response.data)
        .catch((error) => {
          console.log("Error fetching dealer selection discounts set:", error);
          return [];
        }),
    getShippingAddresses: (
      xtremeBffServiceURL: string,
      accountId: string,
      orderType?: string
    ): Promise<SavedAddress[]> => {
      const params = new URLSearchParams({ accountId });
      if (orderType) params.set("orderType", orderType);
      return axios
        .getWithRetries<SavedAddress[]>(
          `${xtremeBffServiceURL}/dealer/shippingAddress?${params.toString()}`
        )
        .then((response) => response.data)
        .catch((error) => Promise.reject(error));
    },

    getUserPreferences: (
      userXid: string,
      accountId?: string,
      mappedAccountIds?: string[]
    ): Promise<UserPreferenceResponse> =>
      axios
        .postWithRetries(`${baseUrl}/preferences/userPreferences`, {
          userXid,
          accountId,
          mappedAccountIds,
        })
        .then((response) => response.data),

    lineValidation: (
      xtremeBffServiceURL: string,
      lineValidationRequest: LineValidationRequest
    ): Promise<LineValidationResponse> =>
      axios
        .postWithRetries(`${xtremeBffServiceURL}/validate/line`, {
          ...lineValidationRequest,
        })
        .then((response) => response.data)
        .catch((error) => Promise.reject(error)),

    linesValidation: (
      xtremeBffServiceURL: string,
      linesValidationRequest: LineValidationRequest[],
      breakOnFailure?: boolean,
      lang?: string
    ): Promise<Record<string, LineValidationResponse>> =>
      axios
        .postWithRetries(
          `${xtremeBffServiceURL}/validate/lines`,
          [...linesValidationRequest],
          {
            params: { lang, breakOnFailure },
          }
        )
        .then((response) => {
          const responseData: Record<string, LineValidationResponse> =
            Object.entries(response.data).reduce((acc, [key, value]) => {
              const partNumberMatch = key.match(/partNumber=([^,]+)/);
              const requestedQuantityMatch = key.match(
                /requestedQuantity=([^,]+)/
              );
              const partNumber = partNumberMatch ? partNumberMatch[1] : "";
              const requestedQuantity = requestedQuantityMatch
                ? requestedQuantityMatch[1]
                : "0";

              const newKey = `${partNumber}-${requestedQuantity}`;
              acc[newKey] = <LineValidationResponse>value;
              return acc;
            }, {} as Record<string, LineValidationResponse>);

          return responseData;
        })
        .catch((error) => Promise.reject(error)),
  };
};
export default useXtremeBffApi;
